import React, { useEffect, useState } from 'react';
import { Lock, Eye, EyeOff, Save, RefreshCw, Loader2, Shield, MapPin as MapIcon, CreditCard, Bell, Settings as SettingsIcon, Languages, Trash2 } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { publicSettings } from '../services/publicSettings';

const SECTIONS = [
  { id: 'general', label: 'General', icon: SettingsIcon, keys: ['app_name', 'support_email', 'currency', 'platform_commission_pct', 'service_fee_pct', 'service_fee_flat_cad', 'delivery_radius_km', 'local_courier_radius_km'] },
  { id: 'maps', label: 'Google Maps', icon: MapIcon, keys: ['google_maps_api_key'] },
  { id: 'stripe', label: 'Stripe payments', icon: CreditCard, keys: ['stripe_publishable_key', 'stripe_secret_key', 'stripe_webhook_secret'] },
  { id: 'notif', label: 'Notifications', icon: Bell, keys: ['email_provider', 'resend_api_key', 'sendgrid_api_key', 'email_from', 'interac_email', 'twilio_account_sid', 'twilio_auth_token', 'twilio_phone_number'] },
  { id: 'whatsapp', label: 'WhatsApp', icon: Bell, keys: ['whatsapp_admin_number', 'twilio_whatsapp_number'] },
];

const SECRET_KEYS = new Set(['stripe_secret_key', 'stripe_webhook_secret', 'twilio_auth_token', 'sendgrid_api_key', 'resend_api_key']);

const SettingsAdminPage = () => {
  const { user } = useAuth();
  const isOAuth = user?.provider === 'google';
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [confirm, setConfirm] = useState({ open: false, password: '' });
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState('general');

  const load = async () => {
    const r = await api.get('/admin/settings');
    setData(r.data); setDraft({});
  };
  useEffect(() => { load(); }, []);

  const get = (k) => (k in draft ? draft[k] : (typeof data?.[k] === 'object' ? '' : data?.[k]));

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const saveAll = async () => {
    if (Object.keys(draft).length === 0) { toast.error('No changes to save'); return; }
    setConfirm({ open: true, password: '' });
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const pwd = isOAuth && !confirm.password ? 'I_CONFIRM' : confirm.password;
      await api.patch('/admin/settings', { updates: draft, password: pwd });
      publicSettings.reset();
      toast.success('Settings saved');
      setConfirm({ open: false, password: '' });
      setEditing(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const fieldFor = (k) => {
    const value = get(k);
    const isSecret = SECRET_KEYS.has(k);
    const meta = typeof data?.[k] === 'object' ? data[k] : null;
    const dirty = k in draft;
    const display = isSecret && !revealed[k] && !dirty && meta
      ? meta.masked || ''
      : (typeof value === 'object' ? '' : (value ?? ''));
    return (
      <div key={k} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start py-3 border-b border-slate-100 last:border-0">
        <div>
          <Label className="text-sm text-slate-700 font-medium capitalize">{k.replace(/_/g, ' ')}</Label>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {isSecret ? (meta?.is_set ? 'Set' : 'Not set') : ' '}
            {dirty && <Badge className="ml-2 bg-amber-50 text-amber-700 border-0 hover:bg-amber-50">Modified</Badge>}
          </div>
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <Input
            type={isSecret && !revealed[k] && !dirty && meta ? 'text' : (isSecret ? 'password' : 'text')}
            value={display}
            disabled={!editing}
            onChange={(e) => set(k, e.target.value)}
            className="h-10"
            placeholder={isSecret ? (meta?.is_set ? 'Already set — enter new to replace' : 'Enter value') : ''}
          />
          {isSecret && editing && (
            <Button variant="outline" size="icon" onClick={() => setRevealed((r) => ({ ...r, [k]: !r[k] }))}>
              {revealed[k] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Platform settings</h1>
          <p className="text-slate-500 mt-1">API keys & configuration that power the app. Password required to save.</p>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => { setDraft({}); setEditing(false); }}>Cancel</Button>
              <Button onClick={saveAll} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="h-4 w-4 mr-1.5" />Save changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
              <Button onClick={() => setEditing(true)} className="bg-slate-900 hover:bg-slate-800 text-white"><Shield className="h-4 w-4 mr-1.5" />Edit settings</Button>
            </>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <nav className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl p-2 h-fit">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setSection(s.id)}
              className={`w-full flex items-center gap-2 px-3 h-10 rounded-lg text-sm font-medium ${section === s.id ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'}`}>
              <s.icon className="h-4 w-4" />{s.label}
            </button>
          ))}
          <TranslationCacheCard />
        </nav>
        <section className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-semibold text-slate-900 mb-2">{SECTIONS.find((s) => s.id === section)?.label}</h2>
          <div className="divide-y divide-slate-100">
            {SECTIONS.find((s) => s.id === section)?.keys.map(fieldFor)}
          </div>
        </section>
      </div>

      <Dialog open={confirm.open} onOpenChange={(o) => setConfirm((c) => ({ ...c, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-emerald-600" />Confirm with password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            For security, please {isOAuth ? 'confirm by typing the phrase below' : 're-enter your account password'} to save platform settings.
          </p>
          <div className="mt-3">
            {isOAuth ? (
              <>
                <Label className="text-xs text-slate-600">Type <span className="font-mono font-semibold">I_CONFIRM</span> to proceed</Label>
                <Input value={confirm.password} onChange={(e) => setConfirm((c) => ({ ...c, password: e.target.value }))} placeholder="I_CONFIRM" className="h-10 mt-1" />
              </>
            ) : (
              <>
                <Label className="text-xs text-slate-600">Account password</Label>
                <Input type="password" value={confirm.password} onChange={(e) => setConfirm((c) => ({ ...c, password: e.target.value }))} className="h-10 mt-1" autoFocus />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm({ open: false, password: '' })}>Cancel</Button>
            <Button onClick={doSave} disabled={saving || !confirm.password} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-1.5" />}
              Confirm & save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsAdminPage;

// Sidebar widget — visible on every settings sub-tab.
const TranslationCacheCard = () => {
  const [stats, setStats] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/admin/translations/stats').then((x) => x.data);
      setStats(r);
    } catch (e) { setStats({ total: 0, by_language: {}, ttl_days: 30 }); }
  };
  useEffect(() => { load(); }, []);

  const clearCache = async () => {
    if (!window.confirm('Clear all cached AI translations? Static dictionary translations are unaffected.')) return;
    setClearing(true);
    try {
      const r = await api.delete('/admin/translations/cache').then((x) => x.data);
      toast.success(`Cleared ${r.deleted} cached translations`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setClearing(false); }
  };

  return (
    <div className="mt-3 border-t border-slate-200 pt-3 px-2" data-testid="translation-cache-card">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5 px-2">
        <Languages className="h-3.5 w-3.5" /> AI translation cache
      </div>
      <div className="px-2 mt-2 text-xs text-slate-600">
        <div className="flex items-center justify-between"><span>Cached labels</span><span className="font-medium text-slate-900">{stats?.total ?? '—'}</span></div>
        <div className="flex items-center justify-between mt-1"><span>TTL</span><span className="font-medium text-slate-900">{stats?.ttl_days ?? 30} days</span></div>
      </div>
      <button onClick={clearCache} disabled={clearing}
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
        data-testid="clear-translation-cache-btn">
        {clearing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        Clear cache
      </button>
    </div>
  );
};
